import type {
  ProductCategoryParams,
  ProductContentsResponses,
} from '@/types/externals/obbsApi/index';
import {
  PRODUCT_CODE_TO_CATEGORY_ID,
  PRODUCT_CONTENT_CONSTANTS,
} from '@/consts/ProductContentConst';

export const useProductContent = () => {
  const allProductContents = useState<ProductContentsResponses.ProductContent[]>(
    'allProductContents',
    () => []
  );
  const topProductContents = useState<ProductContentsResponses.ProductContent[]>(
    'topProductContents',
    () => []
  );

  /**
   * 取得先は API ではなく Nitro のキャッシュ（`usePublicCache`）。
   * 失敗したらページ全体をエラーにする。ステータスは一時的な障害として 503。
   */
  const fetchProductContents = async (): Promise<void> => {
    const fetchAndSetData = async (
      resource: 'product-contents-all' | 'product-contents-top',
      state: Ref<ProductContentsResponses.ProductContent[]>
    ) => {
      const { data, error } = await usePublicCache<ProductContentsResponses.ProductContent[]>(
        resource
      );
      if (error.value) {
        throw createError({
          statusCode: 503,
          statusMessage: 'Service Unavailable',
          message: `Failed to fetch ${resource}: ${error.value.message}`,
          fatal: true,
        });
      }
      state.value = data.value ?? [];
    };

    await Promise.all([
      fetchAndSetData('product-contents-all', allProductContents),
      fetchAndSetData('product-contents-top', topProductContents),
    ]);
  };

  /**
   * 商品のカテゴリID。`PRODUCT_CODE_TO_CATEGORY_ID` を正とし、表に無い商品コードは API の値を使う。
   * API が返す `productContentsCategoryId` は旧3分類のままなので、食品（4）はこの上書きでしか出ない。
   */
  const resolveCategoryId = (productContent: ProductContentsResponses.ProductContent): number =>
    PRODUCT_CODE_TO_CATEGORY_ID[productContent.productCode] ??
    productContent.productContentsCategoryId;

  const getProductContentsByCategory = (
    category: ProductCategoryParams.RequestCategory
  ): ProductContentsResponses.ProductContent[] => {
    if (allProductContents.value.length === 0 || topProductContents.value.length === 0) return [];
    if (category === 'all') return allProductContents.value;
    if (category === 'top') return topProductContents.value;
    const categoryId: number | undefined = PRODUCT_CONTENT_CONSTANTS.ENGLISH_NAME_TO_ID[category];
    if (categoryId === undefined) return [];

    return allProductContents.value.filter(
      (productContent: ProductContentsResponses.ProductContent) =>
        resolveCategoryId(productContent) === categoryId
    );
  };

  return {
    fetchProductContents,
    getProductContentsByCategory,
    resolveCategoryId,
  };
};
